import * as DateFns from "date-fns";
import React, { Fragment, useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";

import Clock from "./Clock";
import { ReactComponent as HelmetSVG } from "./player_helmet.svg";
import {
  FUTURE_PRACTICE,
  LIVE_PRACTICE,
  PAST_PRACTICE,
  PERFORMANCE,
  MANAGE,
  REPORT,
  CONTROLLER_SET,
  MAP,
  SCOPE,
  PRACTICE_TRACKING,
  FILTER_KEYS,
} from "../../config/constants";
import { useInterval, useRouter } from "../../helpers/customHooks";
import EditStartTimeButton from "./EditStartTimeButton";
import {
  cleanControllerSet,
  isEmptyObject,
  text_truncate,
  getTeamProps,
  getPracticeHeadData,
  isCC,
} from "../../helpers/helpers";
import {
  useDataEventsDispatch,
  useDataEventsState,
  useFiltersState,
  usePlayerParticipationState,
  usePlayersDispatch,
  useUserState,
} from "../../store/hooks";
import { PracticeHeadProps, ViewerRoutes } from "./types";
import { DataMeta } from "../../config/commonTypes";
import { TEAMS_ALL } from "../../config/constants";
import { useSelector } from "react-redux";
import { RootState } from "store/reducers";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { useManageDispatch } from "store/hooks/manage";
import { useFlagsDispatch, useFlagsState } from "store/hooks/flags";
import { message } from "antd";

const PracticeHead = ({
  setTargetedReprocess,
  targetedReprocess,
  setTargetedPlayers,
  targetedPlayers,
  dataInProgress,
  setOpenDrawer,
  updateSpeeds,
  setUpdateSpeeds,
  selectedPlayers,
  showUpdateSpeeds,
  setShowUpdateSpeeds,
  setSelectedPlayers,
}: PracticeHeadProps) => {
  const { meta: dataMeta = {} as DataMeta, data, meta } = useDataEventsState();
  const { playerParticipationData } = usePlayerParticipationState();
  const { editPracticeTime } = useDataEventsDispatch();
  const { partialReprocess, getPractice } = useManageDispatch();
  const { getLiveSpeeds } = usePlayersDispatch();
  const { practiceSaved } = useFlagsState();
  const { changePracticeSavedSuccessfullyFlag } = useFlagsDispatch();
  const { location, params, scope, history, forcedScopeForFilter } =
    useRouter<ViewerRoutes>();
  const { tab } = params;
  const user = useUserState();
  const practice = data.manage;
  const eventId = dataMeta?._id;
  const league = meta.league || undefined;
  const type = dataMeta.type;

  const {
    [FILTER_KEYS.POSITIONS]: selectedPosition,
    [FILTER_KEYS.STATUS_TRACKING]: selectedStatus,
  } = useFiltersState(
    forcedScopeForFilter,
    FILTER_KEYS.STATUS_TRACKING,
    FILTER_KEYS.POSITIONS
  );
  const practiceDataUpdate = useSelector(
    (state: RootState) => state.practiceDataUpdate.practiceDataUpdate
  );
  const editableFlag = useSelector(
    (state: RootState) => state.practiceDataUpdate.editableFlag
  );
  const getElapsedTime = useCallback(() => {
    const startTime =
      dataMeta.type === LIVE_PRACTICE &&
      dataMeta.currentDrillIndex < dataMeta.drills.length &&
      dataMeta.drills[dataMeta.currentDrillIndex].startTs
        ? dataMeta.drills[dataMeta.currentDrillIndex].startTs
        : dataMeta.type === PAST_PRACTICE &&
          dataMeta.drills &&
          dataMeta.drills.length > 0
        ? dataMeta.drills[0].startTs
        : dataMeta.startTime;
    const endTime =
      dataMeta.type === PAST_PRACTICE
        ? dataMeta.drills && dataMeta.drills.length > 0
          ? dataMeta.drills[dataMeta.drills.length - 1].endTs
          : dataMeta.endTime
        : new Date().getTime();
    const differenceInMilliseconds = endTime - startTime;
    return differenceInMilliseconds < 0 ? 0 : differenceInMilliseconds;
  }, [
    dataMeta.currentDrillIndex,
    dataMeta.drills,
    dataMeta.endTime,
    dataMeta.startTime,
    dataMeta.type,
  ]);

  const [differenceTime, setDifferenceTime] = useState(getElapsedTime());
  const [live, setLive] = useState<number | null>(null);
  const delayTime = 1000;
  const isMobile = window.matchMedia(
    "only screen and (max-width: 900px)"
  ).matches;
  useInterval(() => setDifferenceTime(getElapsedTime()), live);
  useEffect(() => {
    if (!isMobile && league) {
      getPractice(eventId, league!, false);
    }
  }, [league]);

  useEffect(() => {
    // loadWeather(data && dataMeta && dataMeta.startTime ? DateFns.startOfDay(new Date(dataMeta.startTime)) : 0);
    if (dataMeta.type === LIVE_PRACTICE) {
      const difference = getElapsedTime();
      setTimeout(() => {
        setLive(delayTime);
      }, delayTime - (difference % delayTime));
    } else if (dataMeta.type === PAST_PRACTICE) {
      setDifferenceTime(getElapsedTime());
    }
  }, [
    dataMeta.type,
    getElapsedTime,
    dataMeta.drills,
    practiceDataUpdate.start,
  ]);

  const checkHandler = () => {
    if (targetedReprocess) {
      setTargetedReprocess(false);
      setTargetedPlayers([]);
    } else {
      getPractice(eventId, league!, false);
    }
    setTargetedReprocess(!targetedReprocess);
  };
  const checkUpdateSpeeds = () => {
    if (updateSpeeds) setSelectedPlayers([]);
    setUpdateSpeeds(!updateSpeeds);
  };
  const hidePracticeSavedMessage = () => {
    changePracticeSavedSuccessfullyFlag(false);
  };

  const cancelRetrack = () => {
    setTargetedReprocess(false);
    setTargetedPlayers([]);
  };
  const cancelLiveUpdate = () => {
    setUpdateSpeeds(false);
    setSelectedPlayers([]);
  };
  const handlePartialReprocess = () => {
    partialReprocess(
      dataMeta.gameId,
      targetedPlayers,
      practice,
      eventId,
      league!,
      type
    );
    setTargetedReprocess(false);
    setTargetedPlayers([]);
  };

  const onOpenAlertsClick = () => {
    setOpenDrawer(true);
  };

  if (practiceSaved) {
    message.success(
      {
        content: "Practice Successfully Saved",
        className: "message-pro",
        style: {
          marginTop: "60vh",
        },
      },
      10
    );
    hidePracticeSavedMessage();
  }

  const practiceDate = dataMeta.hasOwnProperty("startTime")
    ? dataMeta.startTime
    : new Date();
  const homeTeam =
    dataMeta &&
    dataMeta.homeTeam &&
    dataMeta.homeTeam.teamId &&
    getTeamProps(dataMeta.homeTeam.teamId);

  const handleEditTimestampSaveClick = (
    practice: DataMeta,
    practiceId: string,
    practiceLeague: string
  ) => {
    cleanControllerSet(CONTROLLER_SET);
    editPracticeTime(practice, practiceId, practiceLeague);
  };
  let positionFilter: string[] = [];
  let statusFilter: string[] = [];
  if (selectedPosition && selectedPosition.length > 0) {
    positionFilter = selectedPosition.map((obj) => obj.value);
  }
  if (selectedStatus && selectedStatus.length > 0) {
    statusFilter = selectedStatus.map((obj) => obj.value);
  }

  const { manageData, playersCount, ballCount, participationCount } =
    getPracticeHeadData(
      scope,
      data,
      positionFilter,
      statusFilter,
      playerParticipationData[dataMeta.gameId]
    );
  const tooltip = (val?: string) => {
    if (val) {
      return (
        <Tooltip id="tool">
          <div style={{ color: "#0A6F9A" }}>{val}</div>
        </Tooltip>
      );
    }
    return <Tooltip hidden={true} id="no_tooltip" />;
  };

  return (
    <>
      <div className={dataMeta.type === LIVE_PRACTICE? "header-p-live" :"header-p"}>
        <Link
          to={{
            pathname: isMobile
              ? (location && location.state && location.state.toReturn) ||
                "/schedule"
              : (location && location.state && location.state.toReturn) ||
                "/practices",
            state: history.location.state,
            search:
              (location && location.state && location.state.toReturnSearch) ||
              "",
          }}
          style={{
            padding: "12px 18px",
            opacity: "0.6",
            cursor: "pointer",
          }}
        >
          <div className={"link-arrow-left"}>
            <img
              src="/assets/images/sprites/left.svg"
              alt=""
              height="13px"
              className="logo"
            />
          </div>
        </Link>
        <div className="p-l-t">
          {dataMeta && dataMeta.homeTeam && (
            <div className="logo-image">
              <img
                src={
                  homeTeam
                    ? `/assets/images/logos/${
                        (homeTeam as (typeof TEAMS_ALL)[0]).teamAbbr
                      }.svg`
                    : `/assets/images/logos/CC.svg`
                }
                alt=""
              />
            </div>
          )}
          <OverlayTrigger
            placement="bottom"
            overlay={tooltip(dataMeta.name)}
            key={2}
          >
            <div>
              <h4 className="h-t-01">
                {!isMobile &&
                isCC(user) &&
                tab === PERFORMANCE &&
                !dataInProgress &&
                dataMeta.type === PAST_PRACTICE
                  ? text_truncate(dataMeta.name, 12)
                  : dataMeta.name}
              </h4>
            </div>
          </OverlayTrigger>

          {
            <>
              {dataMeta.type === FUTURE_PRACTICE ||
              dataMeta.type === LIVE_PRACTICE ? null : (
                <Clock
                  elapsed={differenceTime}
                  live={dataMeta.type === LIVE_PRACTICE}
                />
              )}
              {!isMobile &&
                dataMeta.type === PAST_PRACTICE &&
                tab === PERFORMANCE &&
                isCC(user) &&
                !dataInProgress &&
                league &&
                practice && (
                  <>
                    <div
                      style={{ marginTop: "-4px", marginLeft: "1px" }}
                      className="p-h-targeted"
                    >
                      <input
                        className="styled-checkbox"
                        type="checkbox"
                        id="checkbox"
                        checked={targetedReprocess}
                        onChange={checkHandler}
                      />
                      <label className="check-l" htmlFor="checkbox">
                        TARGETED REPROCESS
                      </label>
                    </div>
                    {targetedReprocess && (
                      <div className="manage-btn-targeted">
                        <button
                          style={
                            targetedPlayers.length === 0
                              ? {
                                  opacity: 0.5,
                                  cursor: "default",
                                  pointerEvents: "none",
                                  color: "#FFFFFF",
                                }
                              : {}
                          }
                          className={"btn-green"}
                          onClick={handlePartialReprocess}
                          disabled={targetedPlayers.length === 0}
                        >
                          Save
                        </button>
                        <button className="btn-delete" onClick={cancelRetrack}>
                          Cancel
                        </button>
                      </div>
                    )}
                  </>
                )}
              <div
                className="rl-t"
                style={{ right: dataMeta.type !== LIVE_PRACTICE ? "18px" : "" }}
              >
                {!editableFlag || dataMeta.type !== FUTURE_PRACTICE
                  ? DateFns.format(
                      new Date(dataMeta.startTime),
                      "MMM dd, yyy, 'Started at' hh:mm aaaa"
                    )
                  : tab === MANAGE &&
                    dataMeta.type === FUTURE_PRACTICE && (
                      <EditStartTimeButton
                        date={practiceDate}
                        practice={manageData}
                        editPracticeTime={handleEditTimestampSaveClick}
                        disabled={isEmptyObject(manageData)}
                      />
                    )}
                {dataMeta.type !== LIVE_PRACTICE ? (
                  <>
                    <label className="l-04">{dataMeta.roofType}</label>
                    <label className="l-05">
                      <b>
                        {dataMeta.intensity.split(",").length === 0
                          ? dataMeta.intensity
                          : dataMeta.intensity.split(",").length === 1
                          ? text_truncate(dataMeta.intensity, 9, "...")
                          : dataMeta.intensity.split(",").length === 2
                          ? text_truncate(
                              dataMeta.intensity.split(",")[0],
                              5,
                              "..."
                            ) + " (+1)"
                          : "Multiple (" +
                            dataMeta.intensity.split(",").length +
                            ")"}
                      </b>{" "}
                      Intensity
                    </label>
                    <label className="l-05">
                      <b>PLAYERS</b>
                      <HelmetSVG />
                      {tab === PRACTICE_TRACKING ? (
                        <span>
                          {participationCount > 0 ? participationCount : "-"}
                        </span>
                      ) : tab === PERFORMANCE ||
                        tab === MANAGE ||
                        tab === REPORT ||
                        tab === MAP ? (
                        <span>{playersCount > 0 ? playersCount : "-"}</span>
                      ) : (
                        <span>{ballCount > 0 ? ballCount : "-"}</span>
                      )}
                    </label>
                  </>
                ) : null}
              </div>
            </>
          }
        </div>
      </div>
      {dataMeta.type === LIVE_PRACTICE && (
        <div className="header-p-02">
          <div className="p-l-t-02">
            <>
              <label className="l-01">
                <img
                  src="/assets/images/rec.svg"
                  alt=""
                  height="15px"
                  className="led-img img-live"
                />
                LIVE
              </label>
              {dataMeta.currentDrill &&
              dataMeta.drills[dataMeta.currentDrillIndex] &&
              dataMeta.drills[dataMeta.currentDrillIndex].startTs ? (
                <Fragment key={1}>
                  <OverlayTrigger
                    placement="bottom"
                    overlay={tooltip(isMobile ? dataMeta.currentDrill : null)}
                    key={1}
                  >
                    <label className="l-02" title={dataMeta.currentDrill}>
                      {isMobile
                        ? text_truncate(
                            dataMeta.currentDrill ? dataMeta.currentDrill : "",
                            7
                          )
                        : text_truncate(
                            dataMeta.currentDrill ? dataMeta.currentDrill : "",
                            40
                          )}
                    </label>
                  </OverlayTrigger>
                </Fragment>
              ) : (
                ""
              )}
            </>

            <Clock
              elapsed={differenceTime}
              live={dataMeta.type === LIVE_PRACTICE}
            />
            {tab === PERFORMANCE && !isMobile && isCC(user) && practice && (
              <>
                <div
                  style={{ marginTop: "-4px", marginLeft: "15px" }}
                  className="p-h-targeted"
                >
                  <input
                    className="styled-checkbox"
                    type="checkbox"
                    id="checkbox"
                    checked={updateSpeeds}
                    onChange={checkUpdateSpeeds}
                  />
                  <label className="check-l" htmlFor="checkbox">
                    LIVE SPEED UPDATE
                  </label>
                  {!updateSpeeds && (
                    <div className="speed-update-btn">
                      <button
                        className="btn-delete"
                        onClick={() => getLiveSpeeds(dataMeta.gameId)}
                      >
                        Log
                      </button>
                    </div>
                  )}
                  {updateSpeeds && (
                    <div className="speed-update-btn">
                      <button
                        style={
                          selectedPlayers.length === 0
                            ? {
                                opacity: 0.5,
                                cursor: "default",
                                pointerEvents: "none",
                                color: "#FFFFFF",
                              }
                            : {}
                        }
                        className={"btn-green"}
                        onClick={() => setShowUpdateSpeeds(true)}
                      >
                        Save
                      </button>
                      <button className="btn-delete" onClick={cancelLiveUpdate}>
                        Cancel
                      </button>
                    </div>
                  )}
                </div>
              </>
            )}
            <div className="rl-t">
              <label className="l-04">{dataMeta.roofType}</label>
              <label className="l-05">
                <b>
                  {dataMeta.intensity.split(",").length === 0
                    ? dataMeta.intensity
                    : dataMeta.intensity.split(",").length === 1
                    ? text_truncate(dataMeta.intensity, 9, "...")
                    : dataMeta.intensity.split(",").length === 2
                    ? text_truncate(
                        dataMeta.intensity.split(",")[0],
                        5,
                        "..."
                      ) + " (+1)"
                    : "Multiple (" + dataMeta.intensity.split(",").length + ")"}
                </b>{" "}
                Intensity
              </label>
              <label className="l-05">
                <b>PLAYERS</b>
                <HelmetSVG />
                <span>{playersCount > 0 ? playersCount : "-"}</span>
              </label>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
export default PracticeHead;
